import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { ToastContainer } from "react-toastify";
import axios from "axios";

import RentalInvoiceEdit from "./RentalInvoiceEdit";
import { toastError } from "../../shared/toastHelper";
import { withoutNestedEntry, mergeNestedEntry } from "./nestedAttributes";

const RentalInvoiceEditContext = createContext();

export function useRentalInvoiceEditContext() {
  return useContext(RentalInvoiceEditContext);
};

const RentalInvoiceEditProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    offers,
    rentalInvoiceTemplates,
  } = props;

  const crmEnabled = props.crmEnabled;
  const organizationCustomFieldDefinitions = props.organizationCustomFieldDefinitions || [];

  const [rentalInvoice, setRentalInvoice] = useState(props.rentalInvoice);
  const [rentalInvoiceFormState, setRentalInvoiceFormState] = useState(rentalInvoice);

  // Serialize every save (the debounced whole-form PUT and the targeted
  // insert/delete PUTs) through a single-flight queue so only one request to this
  // invoice is in flight at a time. Concurrent PUTs have no server-side commit
  // ordering, so overlapping saves could apply a stale full-invoice snapshot
  // (e.g. resurrect a just-deleted row); serializing removes that class.
  const saveChain = useRef(Promise.resolve());
  const enqueueSave = (task) => {
    const run = saveChain.current.then(task);
    saveChain.current = run.catch(() => {});
    return run;
  };

  useEffect(() => {
    const debouncedUpdateRentalInvoiceFormState = _.debounce(() => {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      enqueueSave(() =>
        axios.put(`/teams/${team.id}/rental_invoices/${rentalInvoice.id}`, {
          rental_invoice: rentalInvoiceFormState,
        }).then(({ data }) => {
          setRentalInvoice(data);
        }).catch((error) => {
          toastError(error.response?.data?.join?.(", ") || "Failed to update rental invoice");
        })
      );
    }, 250);

    debouncedUpdateRentalInvoiceFormState();

    return () => {
      debouncedUpdateRentalInvoiceFormState.cancel();
    };
  }, [rentalInvoiceFormState]);

  const insertNested = (bucket, attributes, failureMessage) =>
    enqueueSave(() =>
      axios.put(`/teams/${team.id}/rental_invoices/${rentalInvoice.id}`, {
        rental_invoice: { [bucket]: [attributes] }
      }).then(({ data }) => {
        setRentalInvoice(data);
      }).catch((error) => {
        toastError(error.response?.data?.join?.(", ") || failureMessage);
      })
    );

  const insertInvoiceItem = (attributes) =>
    insertNested("rental_invoice_items_attributes", attributes, "Failed to add item");

  const insertDeposit = (attributes) =>
    insertNested("deposits_attributes", attributes, "Failed to add deposit");

  const insertTaxFee = (attributes) =>
    insertNested("rental_invoice_tax_fees_attributes", attributes, "Failed to add tax/fee");

  // Deletes go through their own targeted PUT (not the accumulating form state)
  // so a _destroy is sent once and never lingers to be re-sent on a later save.
  // Also drop any pending edit for the row so it can't RecordNotFound afterward.
  const deleteNested = (bucket, id) => {
    setRentalInvoiceFormState((prev) => withoutNestedEntry(prev, bucket, id));
    return insertNested(bucket, { id, _destroy: true }, "Failed to remove");
  };

  const updateNestedAttributes = (bucket, entry) => {
    setRentalInvoiceFormState((prev) => mergeNestedEntry(prev, bucket, entry));
  };

  const value = {
    csrfToken,
    user,
    team,
    offers,
    crmEnabled,
    organizationCustomFieldDefinitions,
    rentalInvoice,
    setRentalInvoice,
    rentalInvoiceFormState,
    setRentalInvoiceFormState,
    rentalInvoiceTemplates,
    insertInvoiceItem,
    insertDeposit,
    insertTaxFee,
    updateNestedAttributes,
    deleteNested,
  };

  return (
    <RentalInvoiceEditContext.Provider value={value}>
      {props.children}
    </RentalInvoiceEditContext.Provider>
  );
};

const RentalInvoiceEditContainer = (props) => (
  <RentalInvoiceEditProvider {...props}>
    <ToastContainer icon={false} />
    <RentalInvoiceEdit />
  </RentalInvoiceEditProvider>
);

export default RentalInvoiceEditContainer;
